import { useEffect, useState, useMemo, useCallback } from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";

import { QUERY_WIDTHS } from "@utils/index";
import { TutorialInterface } from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { localStorageService } from "@services/index";
import { Button } from "@components/index";
import { dataTutorials } from "@data/index";

export const FeatureTutorial = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const navigate = useNavigate();
  const isLoading = useCommonStore(({ isLoading }) => isLoading);
  const isDrawerShown = useCommonStore(({ isDrawerShown }) => isDrawerShown);
  const isDomUpdated = useCommonStore(({ isDomUpdated }) => isDomUpdated);
  const setCommonStore = useCommonStore(({ setState }) => setState);

  const [tutorialData, setTutorialData] = useState<TutorialInterface>();
  const [element, setElement] = useState<DOMRect | null>(null);
  const [domElement, setDomElement] = useState<HTMLElement | null>(null);
  const [isModalWindow, setIsModalWindow] = useState(false);
  const [step, setStep] = useState(0);

  const tutorialsData = useMemo(() => [...dataTutorials], []);

  const endTutorial = useCallback(() => {
    localStorageService.removeTutorialName();
    setStep(0);
    setElement(null);
    setTutorialData(undefined);
    document.body.style.overflow = "auto";
  }, []);

  useEffect(() => {
    const tutorialName = localStorageService.getTutorialName();

    setTutorialData(tutorialsData.find(({ name }) => name === tutorialName));
  }, [tutorialsData]);

  useEffect(() => {
    if (isLoading || !tutorialData) {
      return;
    }
    const tutorialItem = tutorialData.items[step];
    if (
      (tutorialItem.justForMobile && !isMobile) ||
      (tutorialItem.justForDesktop && isMobile)
    ) {
      if (step >= tutorialData.items.length - 1) {
        endTutorial();
      } else {
        setStep(step + 1);
      }
      return;
    }

    window.scrollTo(0, 0);

    if (tutorialItem.element === "!modal_window") {
      setIsModalWindow(true);
      document.body.style.overflow = "hidden";
      return;
    }

    const el = document.querySelector(
      `[data-testid="${tutorialItem.element}"]`,
    );
    el?.scrollIntoView({ block: "center" });
    const rect = el?.getBoundingClientRect();
    if (!el || !rect) {
      return;
    }
    setCommonStore({ isDomUpdated: false });
    setDomElement(el as HTMLElement);
    setElement(rect);
    document.body.style.overflow = "hidden";
  }, [
    isLoading,
    location,
    step,
    tutorialData,
    isDrawerShown,
    isDomUpdated,
    setCommonStore,
    isMobile,
    endTutorial,
  ]);

  const handleNextStep = () => {
    if (step >= tutorialData!.items.length - 1) {
      endTutorial();
      return;
    }
    setStep(step + 1);
    setIsModalWindow(false);
    setElement(null);
    if (tutorialData!.items[step].afterClickNext?.redirect) {
      navigate(tutorialData!.items[step].afterClickNext.redirect);
    } else if (tutorialData!.items[step].afterClickNext?.clickOnElement) {
      if (tutorialData!.items[step].afterClickNext?.elementForClick) {
        const el = document.querySelector(
          `[data-testid="${tutorialData!.items[step].afterClickNext?.elementForClick}"]`,
        );
        (el as HTMLElement)?.click();
      } else {
        domElement!.click();
      }
    }
  };

  if (!tutorialData) {
    return null;
  }

  const tutorialItem = tutorialData.items[step];

  if (isModalWindow) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black-70">
        <div
          className={clsx(
            "rounded-2xl bg-white-100",
            isMobile ? "px-4 py-3" : "px-6 py-4",
            tutorialItem.modalWindowWidth
              ? tutorialItem.modalWindowWidth
              : isMobile
                ? "w-3/4"
                : "w-1/3",
            tutorialItem.modalWindowHeight
              ? tutorialItem.modalWindowHeight
              : isMobile
                ? "h-3/4"
                : "h-1/3",
          )}
        >
          <div className="relative h-[calc(100%_-_52px)] overflow-auto">
            {tutorialItem.text}
          </div>
          <div
            className={clsx(
              "mt-3 flex gap-x-4",
              step < tutorialData.items.length - 1
                ? "justify-between"
                : "justify-end",
            )}
          >
            {step < tutorialData.items.length - 1 && (
              <Button
                variant="text"
                title="Skip tutorial"
                height="xsmall"
                className="text-middleGray"
                onClick={endTutorial}
              />
            )}
            <Button
              title={
                step === 0
                  ? "Go"
                  : step === tutorialData.items.length - 1
                    ? "Done"
                    : "Next"
              }
              height="xsmall"
              onClick={handleNextStep}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!element) {
    return null;
  }

  const paddingTop = -(tutorialItem.paddingTop || 0);

  const { top, left, width, height } = element;
  const position: {
    left?: number | string;
    top?: number | string;
    bottom?: number | string;
    right?: number | string;
  } = {};
  const textPosition = isMobile
    ? tutorialItem.textPositionMobile
    : tutorialItem.textPosition;
  switch (textPosition) {
    case "top":
      position.left = left;
      position.bottom = `calc(100% - ${top + paddingTop}px)`;
      break;
    case "left":
      position.top = top + paddingTop;
      position.right = `calc(100% - ${left}px)`;
      break;
    case "right":
      position.top = top + paddingTop;
      position.left = left + width;
      break;
    case "top left":
      position.bottom = `calc(100% - ${top + paddingTop}px)`;
      position.right = `calc(100% - ${left}px)`;
      break;
    default:
      position.left = left;
      position.top = top + height;
      break;
  }

  return (
    <>
      <div
        className="fixed top-0 z-50 w-full bg-black-70"
        style={{ height: element.top - 8 + paddingTop }}
      />
      <div
        className="fixed left-0 z-50 bg-black-70"
        style={{
          width: element.left - 9,
          top: element.top - 8 + paddingTop,
          height: `calc(100% - ${element.top - 8 + paddingTop}px)`,
        }}
      />
      <div
        className="fixed z-50 bg-black-70"
        style={{
          left: element.left + element.width + 9,
          width: `calc(100% - ${element.left + element.width + 9}px)`,
          top: element.top - 8 + paddingTop,
          height: `calc(100% - ${element.top - 8 + paddingTop}px)`,
        }}
      />
      <div
        className="fixed z-50 bg-black-70"
        style={{
          left: element.left - 9,
          width: element.width + 18,
          top: element.top + element.height + 8,
          height: `calc(100% - ${element.top + element.height + 8}px)`,
        }}
      />
      <div
        className="fixed z-50 rounded-[9px] shadow-[0_0_0_3px_#009FDF]"
        style={{
          left: element.left - 9,
          top: element.top - 8 + paddingTop,
          width: element.width + 18,
          height: element.height + 16 - paddingTop,
        }}
      />
      <div
        className={clsx(
          "fixed z-50 rounded-lg bg-white-100 p-4 text-black-100 shadow-md before:absolute before:h-0 before:w-0 before:border-8 before:border-transparent before:content-['']",
          tutorialItem.maxWidth ? tutorialItem.maxWidth : "max-w-[500px]",
          (!textPosition || textPosition === "bottom") &&
            "mt-4 before:-top-4 before:border-b-white-100",
          textPosition === "top" &&
            "mb-4 before:-bottom-4 before:border-t-white-100",
          textPosition === "left" &&
            "mr-4 before:-right-4 before:top-2 before:border-l-white-100",
          textPosition === "right" &&
            "ml-4 before:-left-4 before:top-2 before:border-r-white-100",
          textPosition === "top left" &&
            "mb-2 mr-2 before:-bottom-2 before:-right-2 before:-rotate-45 before:border-t-white-100",
        )}
        style={{
          ...position,
          width: "fit-content",
          height: "fit-content",
        }}
      >
        <div>{tutorialItem.text}</div>
        <div
          className={clsx(
            "mt-6 flex gap-x-4",
            step < tutorialData.items.length - 1
              ? "justify-between"
              : "justify-end",
          )}
        >
          {step < tutorialData.items.length - 1 && (
            <Button
              variant="text"
              title="Skip tutorial"
              height="xsmall"
              className="text-middleGray"
              onClick={endTutorial}
            />
          )}
          <Button
            title={step === tutorialData.items.length - 1 ? "Done" : "Next"}
            height="xsmall"
            onClick={handleNextStep}
          />
        </div>
      </div>
    </>
  );
};
